import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-search-input.css?inline';

/**
 * `<pk-search-input>` — debounced text input with search icon + clear button.
 *
 * Standalone search field, also intended for use inside a
 * `<pk-filter-bar slot="search">` region.
 *
 * Attributes:
 *   - `placeholder` — placeholder text.
 *   - `value`       — current value.
 *   - `name`        — name attribute for the inner input (so pk-filter-bar
 *                     snapshot() can pick it up).
 *   - `debounce-ms` — debounce window in ms (default 200). Invalid values
 *                     fall back to 200.
 *
 * Events:
 *   - `search-change` — `CustomEvent<{ value: string }>` after the debounce
 *                       window. Bubbles + composed.
 *
 * Native `input` / `change` events on the inner input still bubble out so
 * `<pk-filter-bar>` can listen for them via its own debouncer.
 */
export class PkSearchInput extends HTMLElement {
    static readonly tagName = 'pk-search-input';

    static get observedAttributes(): string[] {
        return ['placeholder', 'value', 'name', 'debounce-ms'];
    }

    private readonly _root: ShadowRoot;
    private _value = '';
    private _timer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._value = this.getAttribute('value') ?? '';
        this.render();
    }

    disconnectedCallback(): void {
        if (this._timer !== null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    attributeChangedCallback(name: string, _old: string | null, next: string | null): void {
        if (name === 'value') this._value = next ?? '';
        this.render();
    }

    /** Current value (synchronously read; doesn't wait for debounce). */
    get value(): string { return this._value; }
    set value(v: string) {
        this._value = v;
        this.setAttribute('value', v);
        this.render();
    }

    private _debounceMs(): number {
        const raw = Number(this.getAttribute('debounce-ms'));
        return Number.isFinite(raw) && raw > 0 ? raw : 200;
    }

    private readonly _onInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        this._value = input.value;
        this._scheduleEmit();
        this.render();
    };

    private readonly _onClear = (): void => {
        this._value = '';
        this.render();
        // Focus the input so keyboard users can keep typing.
        const input = this._root.querySelector('input.search') as HTMLInputElement | null;
        input?.focus();
        this._scheduleEmit();
    };

    private _scheduleEmit(): void {
        if (this._timer !== null) clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            this._timer = null;
            this.dispatchEvent(new CustomEvent<{ value: string }>('search-change', {
                detail:   { value: this._value },
                bubbles:  true,
                composed: true,
            }));
        }, this._debounceMs());
    }

    private template(): TemplateResult {
        const placeholder = this.getAttribute('placeholder') ?? 'Search…';
        const name        = this.getAttribute('name') ?? '';
        const showClear   = this._value.length > 0;

        return html`
            <style>${styles}</style>
            <div class="wrap">
                <span class="icon" aria-hidden="true">⌕</span>
                <input
                    class="search"
                    type="search"
                    name=${name}
                    placeholder=${placeholder}
                    .value=${this._value}
                    @input=${this._onInput}
                />
                ${showClear ? html`
                    <button
                        type="button"
                        class="clear-btn"
                        aria-label="Clear search"
                        @click=${this._onClear}
                    >&#x2715;</button>
                ` : ''}
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkSearchInput.tagName)) {
    customElements.define(PkSearchInput.tagName, PkSearchInput);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-search-input': PkSearchInput;
    }
}
